import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Dismiss20Regular,
    Mail16Regular,
    TextBold16Regular,
    TextItalic16Regular,
    TextBulletListLtr16Regular,
    Link16Regular,
    Dismiss12Regular,
} from '@fluentui/react-icons';
import { CustomAvatar } from '../../TaskManagement';
import styles from './EmailComposer.module.css';

const stripHtml = html => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const RecipientPicker = ({ label, labelCentered = false, recipients, setRecipients, allMembers, placeholder }) => {
    const [query, setQuery] = React.useState('');

    const selectedIds = new Set(recipients.map(r => r.user_id));
    const suggestions = (allMembers || [])
        .filter(member => !selectedIds.has(member.user_id))
        .filter(member => {
            const q = query.trim().toLowerCase();
            if (!q) return false;
            return [member.full_name, member.email, member.organisation].join(' ').toLowerCase().includes(q);
        })
        .slice(0, 8);

    const removeRecipient = userId => {
        setRecipients(prev => prev.filter(item => item.user_id !== userId));
    };

    const addRecipient = member => {
        setRecipients(prev => [...prev, member]);
        setQuery('');
    };

    return (
        <div className={styles.fieldSection}>
            <div className={labelCentered ? styles.fieldRowCentered : styles.fieldRow}>
                <div className={labelCentered ? styles.fieldLabelCentered : styles.fieldLabel}>{label}</div>
                <div className={styles.fieldContent}>
                    <div className={styles.chipsRow}>
                        {recipients.map(recipient => (
                            <span key={recipient.user_id} className={styles.recipientChip}>
                                <CustomAvatar name={recipient.full_name} size={18} />
                                <span className={styles.chipText}>
                                    <span className={styles.chipName}>{recipient.full_name}</span>
                                    <span className={styles.chipOrg}>{recipient.organisation}</span>
                                </span>
                                <button
                                    type="button"
                                    className={styles.chipRemove}
                                    onClick={() => removeRecipient(recipient.user_id)}
                                    aria-label={`Remove ${recipient.full_name}`}
                                >
                                    <Dismiss12Regular />
                                </button>
                            </span>
                        ))}
                    </div>
                    <input
                        className={styles.recipientSearch}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder={placeholder}
                    />
                    {suggestions.length > 0 && (
                        <div className={styles.suggestionList}>
                            {suggestions.map(member => (
                                <button
                                    key={member.user_id}
                                    type="button"
                                    className={styles.suggestionItem}
                                    onClick={() => addRecipient(member)}
                                >
                                    {member.full_name} · {member.organisation}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const EmailComposer = ({
    open,
    onClose,
    initialRecipients = [],
    initialSubject = '',
    initialMessage = '',
    allMembers = [],
    sending = false,
    onSend,
}) => {
    const quillRef = React.useRef(null);
    const [toRecipients, setToRecipients] = React.useState([]);
    const [ccRecipients, setCcRecipients] = React.useState([]);
    const [showCc, setShowCc] = React.useState(false);
    const [subject, setSubject] = React.useState('');
    const [messageHtml, setMessageHtml] = React.useState('');

    React.useEffect(() => {
        if (!open) return;
        setToRecipients(initialRecipients || []);
        setCcRecipients([]);
        setShowCc(false);
        setSubject(initialSubject || '');
        setMessageHtml(initialMessage || '');
    }, [open, initialRecipients, initialSubject, initialMessage]);

    const applyFormat = command => {
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;
        editor.focus();
        if (command === 'bold') editor.format('bold', !editor.getFormat().bold);
        if (command === 'italic') editor.format('italic', !editor.getFormat().italic);
        if (command === 'list') editor.format('list', editor.getFormat().list === 'bullet' ? false : 'bullet');
        if (command === 'link') {
            const url = window.prompt('Enter link URL');
            if (url) editor.format('link', url);
        }
    };

    if (!open) return null;

    const organisations = new Set(
        [...toRecipients, ...ccRecipients].map(r => r.organisation).filter(Boolean),
    );
    const messageText = stripHtml(messageHtml);
    const canSend =
        toRecipients.length > 0 && subject.trim().length > 0 && messageText.length > 0 && !sending;

    const handleSend = () => {
        if (!canSend) return;
        onSend?.({
            to: toRecipients,
            cc: ccRecipients,
            subject: subject.trim(),
            messageHtml: messageHtml.trim(),
        });
    };

    return (
        <>
            <div className={styles.overlay} onClick={onClose} />
            <aside className={styles.composer}>
                <header className={styles.header}>
                    <div>
                        <h2 className={styles.title}>Email</h2>
                        <p className={styles.subtitle}>Send an email from Lens</p>
                    </div>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </header>

                <div className={styles.body}>
                    <RecipientPicker
                        label="To"
                        recipients={toRecipients}
                        setRecipients={setToRecipients}
                        allMembers={allMembers}
                        placeholder="Search project members"
                    />

                    {!showCc ? (
                        <div className={styles.addCcRow}>
                            <button type="button" className={styles.addCcButton} onClick={() => setShowCc(true)}>
                                + Add Cc
                            </button>
                        </div>
                    ) : (
                        <RecipientPicker
                            label="Cc"
                            recipients={ccRecipients}
                            setRecipients={setCcRecipients}
                            allMembers={allMembers}
                            placeholder="Search project members"
                        />
                    )}

                    <div className={styles.fieldSection}>
                        <div className={styles.fieldRowCentered}>
                            <div className={styles.fieldLabelCentered}>Subject</div>
                            <div className={styles.fieldContent}>
                                <input
                                    className={styles.subjectInput}
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    placeholder="Subject"
                                />
                            </div>
                        </div>
                    </div>

                    {organisations.size > 1 && (
                        <div className={styles.crossOrgBanner}>
                            Recipients include members from {organisations.size} organisations.
                        </div>
                    )}

                    <div className={styles.messageSection}>
                        <div className={styles.formatToolbar}>
                            <button type="button" className={styles.formatButton} onClick={() => applyFormat('bold')} aria-label="Bold">
                                <TextBold16Regular />
                            </button>
                            <button type="button" className={styles.formatButton} onClick={() => applyFormat('italic')} aria-label="Italic">
                                <TextItalic16Regular />
                            </button>
                            <button type="button" className={styles.formatButton} onClick={() => applyFormat('list')} aria-label="Bullet list">
                                <TextBulletListLtr16Regular />
                            </button>
                            <button type="button" className={styles.formatButton} onClick={() => applyFormat('link')} aria-label="Link">
                                <Link16Regular />
                            </button>
                        </div>
                        <div className={styles.editorWrap}>
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={messageHtml}
                                onChange={setMessageHtml}
                                placeholder="Write your message..."
                            />
                        </div>
                        <p className={styles.composerNote}>
                            Sent via Lens · Replies will go to your registered email address.
                        </p>
                    </div>
                </div>

                <footer className={styles.footer}>
                    <button type="button" className={styles.cancelButton} onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className={styles.sendButton} disabled={!canSend} onClick={handleSend}>
                        <Mail16Regular />
                        {sending ? 'Sending...' : 'Send email'}
                    </button>
                </footer>
            </aside>
        </>
    );
};

export default EmailComposer;
